// DCA · UX-3 + DCA-A1 — Unified rate-time HERO chart (Decline Analyzer).
//
// Spec: docs/domain/review_decline_analyzer_plan.md
//   §3B P0 UX-3  — merge the validation (back-test) chart and the forecast chart
//                  into ONE continuous rate-time canvas: history (scatter) +
//                  fit curve + forecast on a single time axis, with the
//                  validation/back-test window called out by a shaded band.
//   §3A DCA-A1   — chart-side correctness:
//                  * semi-log ⇄ linear Y-axis toggle (an exponential tail must
//                    plot as a straight line on the log axis);
//                  * segmented curve rendering (hyperbolic body + Dmin
//                    exponential tail) with the switch point marked;
//                  * a persistent parameter read-out box (model / b / Di / Dmin
//                    / qi / EUR, with units);
//                  * P10/P50/P90 uncertainty band as a shaded ribbon.
//
// Data contract (service/dcaAnalysis.js):
//   getRecommData  → { history:[[date,val]…], other_models,
//                      dca:{ best_model, qi, di, b, fitted:[[date,val]…],
//                            predicted:[[date,val]…] /* = validation window */ } }
//   getDcaForecast → { history, dca:{ predicted:[[date,val]…] /* = forecast */ },
//                      switch:{ switched, date, q },              /* Dmin switch */
//                      band:{ dates, p90, p50, p10, unit } }      /* P10/P50/P90 */
//
// DCA-A1 wired: the Dmin switch point (markPoint + markLine) and the P10/P90
// uncertainty ribbon are now rendered from getDcaForecast's `switch` / `band`
// keys. Both are OPTIONAL and consumed defensively — when a key is absent (or
// malformed / empty), the chart draws only what it has and surfaces a small
// "pending backend field" hint for the missing piece instead of fabricating.
import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import ReactECharts from "echarts-for-react";
import { Empty } from "antd";

import {
  selectActiveWell,
  selectDcaCommonParams,
  selectDcaUserParams,
  selectDcaRecommParams,
  selectDeclinePhase,
} from "features/decline_analysis/dcaProjectSlice";
import { getRecommData, getDcaForecast } from "service/dcaAnalysis";
import { parseFloatValue } from "utils/dataUtil";

// Phase → petroleum-standard colours (oil green / gas red / water-liquid blue)
// and rate units, matching the existing chart conventions in this app.
const PHASE_COLOR = { oil: "#2E8B57", gas: "#FF6B35", liquid: "#4169E1" };
const RATE_UNIT = { oil: "bbl/m", gas: "Mscf/m", liquid: "bbl/m" };
const QI_UNIT = { oil: "bbl/d", gas: "Mscf/d", liquid: "bbl/d" };

// Log axis cannot show <= 0; clamp to a tiny positive floor so a zero-rate
// month does not blank the whole log plot.
const LOG_FLOOR = 1e-3;
const clampLog = (v) => (v == null ? null : v <= 0 ? LOG_FLOOR : v);

function num(v, d = 2) {
  const n = parseFloatValue(v);
  return n || n === 0 ? Number(n).toFixed(d) : "—";
}

function DeclineHeroChart(props) {
  const { loadingIconActions } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];

  const activeWell = useSelector(selectActiveWell);
  const dcaCommonParams = useSelector(selectDcaCommonParams);
  const dcaRecommParams = useSelector(selectDcaRecommParams);
  const dcaUserParams = useSelector(selectDcaUserParams);
  const declinePhase = useSelector(selectDeclinePhase);

  // DCA-A1: linear ⇄ semi-log Y-axis toggle. Log is the DCA default because an
  // exponential decline is a straight line on it.
  const [yScale, setYScale] = useState("log");

  // Raw series pulled from the two engine endpoints.
  const [history, setHistory] = useState(null); // [[date,val]…] full monthly history
  const [fitted, setFitted] = useState(null); // fit over the model window
  const [validation, setValidation] = useState(null); // back-test / validation predicted
  const [forecast, setForecast] = useState(null); // forward forecast predicted
  // DCA-A1: Modified-Hyperbolic switch point (hyperbolic body → exponential
  // tail) and the P10/P50/P90 rate band, both from getDcaForecast. Backend
  // aligns switch.q / band series to the same unit + forecast date axis as the
  // forecast line, so we plot them directly (no re-scaling here).
  const [fcSwitch, setFcSwitch] = useState(null); // { switched, date, q }
  const [fcBand, setFcBand] = useState(null); // { dates:[], p90:[], p50:[], p10:[], unit }
  const [eur, setEur] = useState(null); // EUR = Σhistory + Σforecast

  // Local readout of the fit parameters (from recomm fit; user tune overrides).
  const [fitParams, setFitParams] = useState({});

  const color = PHASE_COLOR[declinePhase] || "#2E8B57";

  // --- data load ---------------------------------------------------------
  // Validation window (history + fitted + back-test predicted).
  useEffect(() => {
    if (!activeWell?.uwi || !declinePhase) {
      setHistory(null);
      setFitted(null);
      setValidation(null);
      return;
    }
    showLoadingIcon && showLoadingIcon();
    getRecommData(
      {
        wellId: activeWell.uwi,
        declinePhase,
        startDate: dcaCommonParams.startDate,
        validationMonths: dcaCommonParams.validationMonths,
      },
      (result) => {
        setHistory(result?.history || []);
        const dca = result?.dca || {};
        setFitted(dca.fitted || null);
        setValidation(dca.predicted || null);
        setFitParams({
          model: dca.best_model,
          qi: dca.qi,
          di: dca.di,
          b: dca.b,
        });
        hideLoadingIcon && hideLoadingIcon();
      },
      () => {
        setHistory([]);
        setFitted(null);
        setValidation(null);
        hideLoadingIcon && hideLoadingIcon();
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWell, declinePhase, dcaCommonParams.startDate, dcaCommonParams.validationMonths]);

  // Forward forecast segment (uses the user-tuned params when present, else
  // the recommended fit — mirrors ForecastChart's existing precedence).
  useEffect(() => {
    if (!activeWell?.uwi || !declinePhase) {
      setForecast(null);
      setFcSwitch(null);
      setFcBand(null);
      setEur(null);
      return;
    }
    const useUser =
      dcaUserParams.declineModel &&
      parseFloatValue(dcaUserParams.declineRate) !== 0;
    const src = useUser ? dcaUserParams : dcaRecommParams;
    if (!src.declineModel) {
      setForecast(null);
      setFcSwitch(null);
      setFcBand(null);
      setEur(null);
      return;
    }
    if (src.declineModel === "HYP" && (!src.b || parseFloat(src.b) === 0)) {
      return;
    }
    getDcaForecast(
      {
        wellId: activeWell.uwi,
        declinePhase,
        declineModel: src.declineModel,
        startDate: dcaCommonParams.startDate,
        forecastMonths: dcaCommonParams.forecastMonths || 240,
        declineRate: src.declineRate,
        ipRatio: src.ipRatio,
        b: src.b,
        dMin: dcaUserParams.dMin ?? dcaRecommParams.dMin,
      },
      (result) => {
        const pred = result?.dca?.predicted || null;
        setForecast(pred);

        // DCA-A1: Dmin switch point — defensively accept only a well-formed
        // object with a real date; anything else (missing key, switched:false,
        // null date) falls through to the pending hint / no marker.
        const sw = result?.switch;
        setFcSwitch(
          sw && typeof sw === "object" && sw.switched && sw.date ? sw : null
        );

        // DCA-A1: P10/P50/P90 band — require parallel dates + at least the two
        // envelope arrays (p10 upper, p90 lower). Empty/malformed → no band.
        const bd = result?.band;
        const bandOk =
          bd &&
          typeof bd === "object" &&
          Array.isArray(bd.dates) &&
          bd.dates.length > 0 &&
          Array.isArray(bd.p10) &&
          Array.isArray(bd.p90);
        setFcBand(bandOk ? bd : null);

        const hist = result?.history || history || [];
        if (pred) {
          const fSum = pred.reduce((t, it) => t + it[1], 0);
          const hSum = hist.reduce((t, it) => t + it[1], 0);
          setEur(fSum + hSum);
        }
      },
      () => {
        setForecast(null);
        setFcSwitch(null);
        setFcBand(null);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeWell,
    declinePhase,
    dcaCommonParams.startDate,
    dcaCommonParams.forecastMonths,
    dcaRecommParams,
    dcaUserParams,
  ]);

  // Effective read-out params: prefer user-tuned, fall back to recommended fit.
  const readout = useMemo(() => {
    const u = dcaUserParams || {};
    const hasUser = u.declineModel && parseFloatValue(u.declineRate) !== 0;
    const src = hasUser ? u : { ...fitParams, ...dcaRecommParams };
    return {
      model: (hasUser ? u.declineModel : fitParams.model) || dcaRecommParams.declineModel,
      qi: src.declineRate ?? fitParams.qi,
      di: src.ipRatio ?? fitParams.di,
      b: src.b ?? fitParams.b,
      dMin: u.dMin ?? dcaRecommParams.dMin,
    };
  }, [dcaUserParams, dcaRecommParams, fitParams]);

  // --- assemble one continuous time axis --------------------------------
  // History (scatter) + fit (line, over the history window) + forecast
  // (line, after history). The back-test / validation window is highlighted
  // with a markArea band. A single category x-axis keeps all three aligned.
  const chart = useMemo(() => {
    if (!history || history.length === 0) return null;

    const histDates = history.map((it) => String(it[0]).slice(0, 10));
    // Forecast continues after the last history month.
    const fcDates = (forecast || []).map((it) => String(it[0]).slice(0, 10));
    // De-dup the join month if the forecast starts on the last history date.
    const fcStart =
      fcDates.length && fcDates[0] === histDates[histDates.length - 1] ? 1 : 0;
    const xAxis = [...histDates, ...fcDates.slice(fcStart)];

    const pad = (arr, offset) => {
      const out = new Array(xAxis.length).fill(null);
      (arr || []).forEach((v, i) => {
        const idx = offset + i;
        if (idx >= 0 && idx < out.length) out[idx] = v;
      });
      return out;
    };

    const log = yScale === "log";
    const map = (series, get) => (series || []).map(get);

    // Historical scatter (aligned from the head of the axis).
    const histVals = pad(
      map(history, (it) => (log ? clampLog(it[1]) : it[1])),
      0
    );

    // Fit line: `fitted` covers the model window inside history. Align it to the
    // tail of the history segment (the fit ends where history ends / forecast
    // begins), matching the existing validation-chart alignment convention.
    const fitOffset = Math.max(0, histDates.length - (fitted?.length || 0));
    const fitVals = pad(
      map(fitted, (it) => (log ? clampLog(it[1]) : it[1])),
      fitOffset
    );

    // Validation (back-test) predicted: occupies the last `validation.length`
    // months of the history window — this is the shaded band.
    const valLen = validation?.length || 0;
    const valStart = Math.max(0, histDates.length - valLen);
    const valVals = pad(
      map(validation, (it) => (log ? clampLog(it[1]) : it[1])),
      valStart
    );

    // Forecast line: aligns to the join — the first forecast point sits at the
    // end of the history window so the fit and forecast render as one curve.
    const fcValsAligned = pad(
      map(forecast, (it) => (log ? clampLog(it[1]) : it[1])),
      histDates.length - fcStart
    );

    // DCA-A1 · P10/P50/P90 band --------------------------------------------
    // The band dates are "YYYY-MM" (forecast calendar); the chart x-axis is
    // "YYYY-MM-DD". Map each band point onto the axis by year-month prefix so
    // it rides the forecast segment regardless of day-of-month. Values are
    // already in the forecast unit (BE-aligned), so only the log clamp applies.
    // ECharts band idiom: draw the LOWER envelope (p90, conservative) as an
    // invisible base line, then STACK the (p10 − p90) thickness on top with an
    // areaStyle — the fill lands exactly between p90 and p10.
    let bandLower = null;
    let bandThick = null;
    let bandMid = null;
    const bandDates = fcBand?.dates || [];
    if (bandDates.length) {
      // ym → axis index (last occurrence wins; forecast months are unique).
      const ymIndex = new Map();
      xAxis.forEach((d, i) => ymIndex.set(String(d).slice(0, 7), i));
      const lower = new Array(xAxis.length).fill(null); // p90
      const thick = new Array(xAxis.length).fill(null); // p10 − p90
      const mid = new Array(xAxis.length).fill(null); // p50
      const p10 = fcBand.p10 || [];
      const p90 = fcBand.p90 || [];
      const p50 = fcBand.p50 || [];
      bandDates.forEach((d, i) => {
        const idx = ymIndex.get(String(d).slice(0, 7));
        if (idx == null) return;
        const lo = parseFloatValue(p90[i]);
        const hi = parseFloatValue(p10[i]);
        if (lo == null && hi == null) return;
        // Clamp both envelopes to positive on the log axis so the ribbon stays
        // valid; keep the raw (p10 − p90) thickness on the linear axis.
        const loC = log ? clampLog(lo) : lo;
        const hiC = log ? clampLog(hi) : hi;
        if (loC != null) lower[idx] = loC;
        if (loC != null && hiC != null) {
          // Stack thickness must be non-negative; guard against p10<p90.
          thick[idx] = log
            ? Math.max(hiC, loC) - Math.min(hiC, loC)
            : Math.max(0, hiC - loC);
        }
        if (p50.length) {
          const m = parseFloatValue(p50[i]);
          if (m != null) mid[idx] = log ? clampLog(m) : m;
        }
      });
      bandLower = lower;
      bandThick = thick;
      bandMid = p50.length ? mid : null;
    }

    // DCA-A1 · switch-point markers, hung on the Forecast series. markLine =
    // vertical dashed guide at the switch date; markPoint = a diamond at the
    // (date, q) where the hyperbolic body hands off to the exponential tail.
    // Only when the axis actually contains that date and q is finite.
    let switchMarkLine;
    let switchMarkPoint;
    if (fcSwitch?.switched && fcSwitch.date) {
      const swYm = String(fcSwitch.date).slice(0, 7);
      const swIdx = xAxis.findIndex((d) => String(d).slice(0, 7) === swYm);
      const swQraw = parseFloatValue(fcSwitch.q);
      if (swIdx >= 0) {
        const swX = xAxis[swIdx];
        switchMarkLine = {
          silent: true,
          symbol: "none",
          lineStyle: { color: "#FFD166", width: 1, type: "dashed" },
          label: {
            show: true,
            position: "insideEndTop",
            color: "#FFD166",
            fontSize: 10,
            formatter: "Dmin switch",
          },
          data: [{ xAxis: swX }],
        };
        if (swQraw != null) {
          const swQ = log ? clampLog(swQraw) : swQraw;
          switchMarkPoint = {
            silent: true,
            symbol: "diamond",
            symbolSize: 11,
            itemStyle: {
              color: "#FFD166",
              borderColor: "#7a5c00",
              borderWidth: 1,
            },
            label: { show: false },
            data: [{ coord: [swX, swQ], value: "Dmin switch" }],
          };
        }
      }
    }

    const series = [
      // Band base + fill first so it renders UNDER history / fit / forecast.
      ...(bandLower
        ? [
            {
              name: "P90 (lower)",
              type: "line",
              data: bandLower,
              stack: "p10p90band",
              showSymbol: false,
              symbol: "none",
              lineStyle: { opacity: 0 },
              itemStyle: { color },
              tooltip: { show: false },
              silent: true,
              z: 1,
            },
            {
              name: "P10–P90 band",
              type: "line",
              data: bandThick,
              stack: "p10p90band",
              showSymbol: false,
              symbol: "none",
              lineStyle: { opacity: 0 },
              // Phase colour, low-alpha so it never overpowers history/fit.
              areaStyle: { color, opacity: 0.16 },
              itemStyle: { color },
              tooltip: { show: false },
              silent: true,
              z: 1,
            },
          ]
        : []),
      ...(bandMid
        ? [
            {
              name: "P50",
              type: "line",
              data: bandMid,
              showSymbol: false,
              symbol: "none",
              lineStyle: { color, width: 1, type: "dotted", opacity: 0.7 },
              itemStyle: { color },
              z: 2,
            },
          ]
        : []),
      {
        name: "Historical",
        type: "scatter",
        data: histVals,
        symbolSize: 4,
        itemStyle: { color, opacity: 0.75 },
        z: 3,
      },
      {
        name: "Fit",
        type: "line",
        data: fitVals,
        showSymbol: false,
        smooth: false,
        lineStyle: { color, width: 2 },
        itemStyle: { color },
        z: 4,
        // The validation window shading rides on the fit series' markArea.
        markArea:
          valLen > 0
            ? {
                silent: true,
                itemStyle: { color: "rgba(255,255,255,0.06)" },
                label: {
                  show: true,
                  position: "insideTop",
                  color: "rgba(255,255,255,0.5)",
                  fontSize: 10,
                  formatter: "Back-test",
                },
                data: [
                  [
                    { xAxis: xAxis[valStart] },
                    { xAxis: xAxis[histDates.length - 1] },
                  ],
                ],
              }
            : undefined,
      },
      {
        name: "Back-test",
        type: "line",
        data: valVals,
        showSymbol: false,
        lineStyle: { color, width: 2, type: "dashed" },
        itemStyle: { color },
        z: 4,
      },
      {
        name: "Forecast",
        type: "line",
        data: fcValsAligned,
        showSymbol: false,
        lineStyle: { color, width: 2 },
        itemStyle: { color },
        z: 4,
        markLine: switchMarkLine,
        markPoint: switchMarkPoint,
      },
    ];

    const option = {
      backgroundColor: "transparent",
      grid: { left: "5%", right: "4%", top: 44, bottom: 48, containLabel: true },
      legend: {
        top: 8,
        textStyle: { color: "#ccc" },
        // Only advertise the band / P50 entries when they were actually drawn.
        data: [
          "Historical",
          "Fit",
          "Back-test",
          "Forecast",
          ...(bandLower ? ["P10–P90 band"] : []),
          ...(bandMid ? ["P50"] : []),
        ],
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { snap: true, label: { precision: 2 } },
      },
      toolbox: {
        right: 10,
        top: 4,
        itemSize: 16,
        feature: {
          dataZoom: { yAxisIndex: "none" },
          restore: {},
          saveAsImage: { type: "png" },
        },
      },
      dataZoom: [{ type: "inside" }, { type: "slider", height: 16, bottom: 20 }],
      xAxis: {
        type: "category",
        data: xAxis,
        boundaryGap: false,
        axisLabel: {
          color: "#aaa",
          formatter: (v) => {
            const d = new Date(v);
            return isNaN(d)
              ? v
              : `${("0" + (d.getMonth() + 1)).slice(-2)}/${d.getFullYear()}`;
          },
        },
      },
      yAxis: {
        type: log ? "log" : "value",
        name: `Rate (${RATE_UNIT[declinePhase] || ""})`,
        nameTextStyle: { color: "#aaa" },
        min: log ? LOG_FLOOR : undefined,
        axisLabel: { color: "#aaa" },
        splitLine: { lineStyle: { opacity: 0.15, color: "#fff" } },
      },
      series,
    };
    return option;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    history,
    fitted,
    validation,
    forecast,
    fcBand,
    fcSwitch,
    yScale,
    declinePhase,
    color,
  ]);

  const toggleScale = useCallback(
    () => setYScale((s) => (s === "log" ? "value" : "log")),
    []
  );

  const hasData = history && history.length > 0;

  return (
    <div className="cards-container h-100">
      <div className="cards-header d-flex flex-row justify-content-between align-items-center">
        <span className="header-title">Decline · Rate–Time</span>
        <div className="d-flex align-items-center">
          {/* DCA-A1: semi-log ⇄ linear Y-axis toggle */}
          <span
            role="button"
            onClick={toggleScale}
            className="btn-custom"
            title="Toggle linear / semi-log Y axis (exponential decline is a straight line on log)"
            style={{ cursor: "pointer" }}
          >
            Y: {yScale === "log" ? "Log" : "Linear"}
          </span>
        </div>
      </div>
      <div
        className="cards-content"
        style={{ position: "relative", padding: 0 }}
      >
        {hasData ? (
          <ReactECharts
            option={chart}
            notMerge={true}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ color: "rgba(255,255,255,0.6)", paddingTop: 80 }}
          >
            No production / forecast for this well.
          </Empty>
        )}

        {/* DCA-A1: persistent parameter read-out box, overlaid top-left. */}
        {hasData && (
          <div
            style={{
              position: "absolute",
              top: 44,
              left: 24,
              background: "rgba(20,24,48,0.82)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 6,
              padding: "8px 12px",
              fontSize: 12,
              color: "#dfe3ff",
              pointerEvents: "none",
              lineHeight: 1.5,
              zIndex: 2,
            }}
          >
            <div>
              <b>Model</b>: {readout.model || "—"}
            </div>
            <div>
              qi: {num(readout.qi)} {QI_UNIT[declinePhase]}
            </div>
            <div>Di: {num(readout.di, 3)} %/yr</div>
            <div>b: {num(readout.b, 3)}</div>
            <div>Dmin: {num(readout.dMin, 2)} %/yr</div>
            <div>
              EUR: {eur != null ? num(eur, 0) : "—"} {RATE_UNIT[declinePhase]?.split("/")[0]}
            </div>
          </div>
        )}

        {/* DCA-A1: the switch-point marker and P10/P90 band are now drawn from
            the forecast response. Surface the "pending backend field" hint ONLY
            for whichever piece is still missing — once both are present the hint
            disappears entirely. A forecast with switched:false is a valid "no
            switch" answer, not a missing field, so it does not trigger the hint. */}
        {hasData &&
          forecast &&
          (() => {
            const missing = [];
            if (!fcBand) missing.push("P10/P50/P90 band");
            if (!fcSwitch) missing.push("Dmin switch-point");
            if (!missing.length) return null;
            return (
              <div
                style={{
                  position: "absolute",
                  bottom: 44,
                  right: 16,
                  fontSize: 10,
                  color: "rgba(255,255,255,0.45)",
                  pointerEvents: "none",
                  textAlign: "right",
                  maxWidth: 260,
                }}
              >
                {missing.join(" & ")} pending backend field
                {missing.length > 1 ? "s" : ""}.
              </div>
            );
          })()}
      </div>
    </div>
  );
}

export default DeclineHeroChart;
